/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { CHAT_MESSAGE_MAX_CHARS } from '../../services/aiInsightsApi';
import { formatAskEvansAnswerMarkdown } from '../../utils/askEvansMarkdownFormat';

/**
 * Normalize assistant content (string | {text|content} | array) like Lessons Learned MarkdownBody.
 */
export const toMessageText = (value) => {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(toMessageText).filter(Boolean).join('\n\n');
  if (typeof value === 'object') {
    if (typeof value.text === 'string') return value.text;
    if (typeof value.content === 'string') return value.content;
    if (typeof value.markdown === 'string') return value.markdown;
    if (typeof value.answer === 'string') return value.answer;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '';
    }
  }
  return '';
};

const decodeEntities = (s) =>
  String(s)
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

/** Never render HTML — strip/convert tags to markdown (Lessons Learned sister). */
const htmlToMarkdown = (html) => {
  let s = String(html);
  s = s.replace(/\r\n/g, '\n');
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<\/(p|div|tr|h[1-6]|li|ul|ol|table|thead|tbody)>/gi, '\n');
  s = s.replace(/<h([1-6])[^>]*>/gi, (_, n) => `${'#'.repeat(Number(n))} `);
  s = s.replace(/<(strong|b)[^>]*>/gi, '**');
  s = s.replace(/<\/(strong|b)>/gi, '**');
  s = s.replace(/<(em|i)[^>]*>/gi, '*');
  s = s.replace(/<\/(em|i)>/gi, '*');
  s = s.replace(/<code[^>]*>/gi, '`');
  s = s.replace(/<\/code>/gi, '`');
  s = s.replace(/<li[^>]*>/gi, '- ');
  s = s.replace(/<th[^>]*>/gi, '| ');
  s = s.replace(/<\/th>/gi, ' ');
  s = s.replace(/<td[^>]*>/gi, '| ');
  s = s.replace(/<\/td>/gi, ' ');
  s = s.replace(/<hr\s*\/?>/gi, '\n---\n');
  s = s.replace(/<[^>]+>/g, '');
  return decodeEntities(s)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const looksLikeHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s);

const formatCell = (v) => {
  if (v == null) return '';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return '';
    }
  }
  return String(v).replace(/\|/g, '\\|').replace(/\n/g, ' ');
};

/** Convert JSON payloads into markdown (tables / lists / headings) — never dump raw HTML. */
const JSON_TO_MD_MAX_DEPTH = 12;
const INLINE_FORMAT_MAX_CHARS = 12_000;
const BEAUTIFY_JSON_MAX_CHARS = 400_000;

export const jsonToMarkdown = (value, depth = 0) => {
  if (depth > JSON_TO_MD_MAX_DEPTH) return '…';
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (Array.isArray(value)) {
    if (!value.length) return '_No rows._';
    const objects = value.every((v) => v && typeof v === 'object' && !Array.isArray(v));
    if (objects) {
      const keys = [];
      value.forEach((row) => {
        Object.keys(row).forEach((k) => {
          if (!keys.includes(k)) keys.push(k);
        });
      });
      const header = `| ${keys.join(' | ')} |`;
      const sep = `| ${keys.map(() => '---').join(' | ')} |`;
      const rows = value.map(
        (row) => `| ${keys.map((k) => formatCell(row[k])).join(' | ')} |`
      );
      return [header, sep, ...rows].join('\n');
    }
    return value
      .map((item) => {
        if (item && typeof item === 'object') {
          return `- ${formatCell(item)}`;
        }
        return `- ${String(item)}`;
      })
      .join('\n');
  }

  if (typeof value === 'object') {
    if (typeof value.markdown === 'string') return value.markdown;
    if (typeof value.answer === 'string') return value.answer;
    if (typeof value.text === 'string') return value.text;
    if (typeof value.content === 'string') return value.content;

    if (Array.isArray(value.tables) || Array.isArray(value.table)) {
      const tables = value.tables || value.table;
      const parts = [];
      if (value.title) parts.push(`## ${value.title}`);
      if (value.summary || value.overview) {
        parts.push(String(value.summary || value.overview));
      }
      (Array.isArray(tables) ? tables : [tables]).forEach((t) => {
        if (!t) return;
        if (t.title) parts.push(`### ${t.title}`);
        if (Array.isArray(t.headers) && Array.isArray(t.rows)) {
          const header = `| ${t.headers.join(' | ')} |`;
          const sep = `| ${t.headers.map(() => '---').join(' | ')} |`;
          const rows = t.rows.map(
            (row) =>
              `| ${(Array.isArray(row) ? row : t.headers.map((h) => row[h])).map(formatCell).join(' | ')} |`
          );
          parts.push([header, sep, ...rows].join('\n'));
        } else if (Array.isArray(t.rows)) {
          parts.push(jsonToMarkdown(t.rows, depth + 1));
        } else {
          parts.push(jsonToMarkdown(t, depth + 1));
        }
      });
      if (Array.isArray(value.sections)) {
        value.sections.forEach((sec) => {
          if (!sec) return;
          if (typeof sec === 'string') parts.push(sec);
          else if (sec.title) {
            parts.push(`### ${sec.title}`);
            parts.push(jsonToMarkdown(sec.body ?? sec.content ?? sec.items ?? '', depth + 1));
          } else parts.push(jsonToMarkdown(sec, depth + 1));
        });
      }
      return parts.filter(Boolean).join('\n\n');
    }

    const entries = Object.entries(value);
    const scalars = entries.every(
      ([, v]) => v == null || ['string', 'number', 'boolean'].includes(typeof v)
    );
    if (scalars && entries.length) {
      const header = '| Metric | Value |';
      const sep = '| --- | --- |';
      const rows = entries.map(
        ([k, v]) => `| ${formatCell(k)} | ${formatCell(v)} |`
      );
      return [header, sep, ...rows].join('\n');
    }

    return entries
      .map(([k, v]) => {
        const heading = depth === 0 ? `### ${k}` : `#### ${k}`;
        const body = jsonToMarkdown(v, depth + 1);
        return `${heading}\n\n${body}`;
      })
      .join('\n\n');
  }

  return String(value);
};

/**
 * Turn consecutive `**Key:** value` / `Key: value` lines into a GFM table
 * (Quick Snapshot style → proper table).
 */
const keyValueRunsToTables = (text) => {
  const lines = text.split('\n');
  const out = [];
  let i = 0;
  const kvRe = /^(?:\*\*)?([^*:\n][^:\n]{0,80}?)(?:\*\*)?\s*:\s+(.+)$/;

  while (i < lines.length) {
    const trimmed = lines[i].trim();
    const m = trimmed.match(kvRe);
    if (
      m
      && !trimmed.startsWith('|')
      && !trimmed.startsWith('#')
      && !trimmed.startsWith('-')
      && !trimmed.startsWith('*')
    ) {
      const rows = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        if (!t) {
          if (rows.length) break;
          i += 1;
          continue;
        }
        const km = t.match(kvRe);
        if (!km || t.startsWith('|') || t.startsWith('#') || t.startsWith('-') || t.startsWith('*')) {
          break;
        }
        rows.push([km[1].trim(), km[2].trim()]);
        i += 1;
      }
      if (rows.length >= 2) {
        out.push('| Metric | Value |');
        out.push('| --- | --- |');
        rows.forEach(([k, v]) => out.push(`| ${formatCell(k)} | ${formatCell(v)} |`));
        out.push('');
        continue;
      }
      if (rows.length === 1) {
        const [k, v] = rows[0];
        out.push(`**${k}:** ${v}`);
        continue;
      }
      // Matched KV pattern at line i but inner pass collected nothing — advance.
      out.push(lines[i]);
      i += 1;
      continue;
    }
    out.push(lines[i]);
    i += 1;
  }
  return out.join('\n');
};

/** Prep assistant content: JSON → markdown tables, HTML → text, key-values → tables. */
export const beautifyAskEvansContent = (content) => {
  let raw = toMessageText(content).replace(/\r\n/g, '\n').trim();
  if (!raw) return '';

  // Parse JSON string payloads into structured markdown (not a code dump)
  if (
    raw.length <= BEAUTIFY_JSON_MAX_CHARS &&
    ((raw.startsWith('{') && raw.endsWith('}')) ||
    (raw.startsWith('[') && raw.endsWith(']')))
  ) {
    try {
      const parsed = JSON.parse(raw);
      raw = jsonToMarkdown(parsed);
    } catch {
      /* keep raw */
    }
  } else if (looksLikeHtml(raw)) {
    raw = htmlToMarkdown(raw);
  }

  return formatAskEvansAnswerMarkdown(keyValueRunsToTables(raw).trim());
};

const inlineFormat = (text) => {
  if (text.length > INLINE_FORMAT_MAX_CHARS) {
    return text;
  }
  const parts = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = re.exec(text)) != null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={`b-${key++}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      parts.push(<em key={`i-${key++}`}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith('`')) {
      parts.push(
        <code key={`c-${key++}`} className="spacer-log-md__code">
          {token.slice(1, -1)}
        </code>
      );
    }
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
};

const isTableSep = (line) => /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line.trim());

const splitRow = (line) => {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
};

/**
 * Lessons Learned sister markdown: headings, lists, bold, code, GFM pipe tables.
 * JSON/HTML answers are beautified to structured markdown — never rendered as HTML.
 */
const SpacerLogMarkdown = ({ content }) => {
  const nodes = useMemo(() => {
    let text;
    try {
      text = beautifyAskEvansContent(content);
    } catch {
      text = toMessageText(content);
    }
    if (!text) return [<p key="empty" className="spacer-log-md__muted">No content.</p>];

    if (text.length > CHAT_MESSAGE_MAX_CHARS) {
      return [
        <pre key="plain" className="spacer-log-md__pre">
          <code className="spacer-log-md__code-block">{text.slice(0, CHAT_MESSAGE_MAX_CHARS)}…</code>
        </pre>,
      ];
    }

    const lines = text.split('\n');
    const out = [];
    let listBuf = [];
    let listType = null;
    let codeBuf = null;
    let key = 0;

    const flushList = () => {
      if (!listBuf.length) return;
      const Tag = listType === 'ol' ? 'ol' : 'ul';
      out.push(
        <Tag key={`list-${key++}`} className="spacer-log-md__list">
          {listBuf.map((item, i) => {
            const parts = String(item).split('\n');
            const head = parts[0] || '';
            const subs = parts
              .slice(1)
              .map((s) => s.replace(/^•\s*/, '').trim())
              .filter(Boolean);
            return (
              <li key={i}>
                {inlineFormat(head)}
                {subs.length ? (
                  <ul className="spacer-log-md__list spacer-log-md__list--nested">
                    {subs.map((sub, j) => (
                      <li key={j}>{inlineFormat(sub)}</li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </Tag>
      );
      listBuf = [];
      listType = null;
    };

    const flushCode = () => {
      if (codeBuf == null) return;
      const joined = codeBuf.join('\n').trim();
      if (
        (joined.startsWith('{') && joined.endsWith('}')) ||
        (joined.startsWith('[') && joined.endsWith(']'))
      ) {
        try {
          const md = beautifyAskEvansContent(jsonToMarkdown(JSON.parse(joined)));
          codeBuf = null;
          // Expand beautified markdown into the same node stream
          const sub = md.split('\n');
          for (let si = 0; si < sub.length; si += 1) {
            const st = sub[si].trim();
            if (!st) continue;
            if (st.includes('|') && si + 1 < sub.length && isTableSep(sub[si + 1])) {
              const header = splitRow(st);
              si += 1;
              const body = [];
              while (
                si + 1 < sub.length &&
                sub[si + 1].trim().includes('|') &&
                !isTableSep(sub[si + 1])
              ) {
                si += 1;
                body.push(splitRow(sub[si].trim()));
              }
              out.push(
                <div key={`tbl-${key++}`} className="ask-evans-table-wrap">
                  <table className="ask-evans-table">
                    <thead>
                      <tr>
                        {header.map((h, hi) => (
                          <th key={hi}>{inlineFormat(h)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {body.map((row, ri) => (
                        <tr key={ri}>
                          {header.map((_, ci) => (
                            <td key={ci}>{inlineFormat(row[ci] || '')}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
              continue;
            }
            if (st.startsWith('### ')) {
              out.push(
                <h4 key={`h-${key++}`} className="spacer-log-md__h4">
                  {inlineFormat(st.slice(4))}
                </h4>
              );
              continue;
            }
            if (st.startsWith('## ')) {
              out.push(
                <h3 key={`h-${key++}`} className="spacer-log-md__h3">
                  {inlineFormat(st.slice(3))}
                </h3>
              );
              continue;
            }
            const ulm = st.match(/^[-*]\s+(.+)$/);
            if (ulm) {
              out.push(
                <ul key={`ul-${key++}`} className="spacer-log-md__list">
                  <li>{inlineFormat(ulm[1])}</li>
                </ul>
              );
              continue;
            }
            out.push(
              <p key={`p-${key++}`} className="spacer-log-md__p">
                {inlineFormat(st)}
              </p>
            );
          }
          return;
        } catch {
          /* fall through */
        }
      }
      out.push(
        <pre key={`pre-${key++}`} className="spacer-log-md__pre">
          <code className="spacer-log-md__code-block">{codeBuf.join('\n')}</code>
        </pre>
      );
      codeBuf = null;
    };

    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      const trimmed = line.trim();

      if (codeBuf != null) {
        if (trimmed.startsWith('```')) {
          flushCode();
        } else {
          codeBuf.push(line);
        }
        continue;
      }

      if (trimmed.startsWith('```')) {
        flushList();
        codeBuf = [];
        continue;
      }

      // GFM table: header + separator + rows
      if (
        trimmed.includes('|') &&
        i + 1 < lines.length &&
        isTableSep(lines[i + 1])
      ) {
        flushList();
        const header = splitRow(trimmed);
        i += 1; // skip separator
        const body = [];
        while (i + 1 < lines.length && lines[i + 1].trim().includes('|') && !isTableSep(lines[i + 1])) {
          i += 1;
          body.push(splitRow(lines[i].trim()));
        }
        out.push(
          <div key={`tbl-${key++}`} className="ask-evans-table-wrap">
            <table className="ask-evans-table">
              <thead>
                <tr>
                  {header.map((h, hi) => (
                    <th key={hi}>{inlineFormat(h)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((row, ri) => (
                  <tr key={ri}>
                    {header.map((_, ci) => (
                      <td key={ci}>{inlineFormat(row[ci] || '')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }

      if (!trimmed) {
        let j = i + 1;
        while (j < lines.length && !lines[j].trim()) j += 1;
        const nextTrimmed = j < lines.length ? lines[j].trim() : '';
        const continuesList =
          listBuf.length > 0
          && (nextTrimmed.match(/^\d+\.\s+/) || nextTrimmed.match(/^[-*+]\s+/));
        if (!continuesList) flushList();
        continue;
      }

      const ul = trimmed.match(/^[-*+]\s+(.+)$/);
      const ol = trimmed.match(/^\d+\.\s+(.+)$/);
      if (ul) {
        if (listType === 'ol' && listBuf.length) {
          listBuf[listBuf.length - 1] = `${listBuf[listBuf.length - 1]}\n• ${ul[1]}`;
          continue;
        }
        if (listType && listType !== 'ul') flushList();
        listType = 'ul';
        listBuf.push(ul[1]);
        continue;
      }
      if (ol) {
        if (listType && listType !== 'ol') flushList();
        listType = 'ol';
        listBuf.push(ol[1]);
        continue;
      }

      flushList();

      if (trimmed.startsWith('### ')) {
        out.push(
          <h4 key={`h-${key++}`} className="spacer-log-md__h4">
            {inlineFormat(trimmed.slice(4))}
          </h4>
        );
        continue;
      }
      if (trimmed.startsWith('## ')) {
        out.push(
          <h3 key={`h-${key++}`} className="spacer-log-md__h3">
            {inlineFormat(trimmed.slice(3))}
          </h3>
        );
        continue;
      }
      if (trimmed.startsWith('# ')) {
        out.push(
          <h2 key={`h-${key++}`} className="spacer-log-md__h2">
            {inlineFormat(trimmed.slice(2))}
          </h2>
        );
        continue;
      }
      if (trimmed.startsWith('> ')) {
        out.push(
          <blockquote key={`q-${key++}`} className="spacer-log-md__quote">
            {inlineFormat(trimmed.slice(2))}
          </blockquote>
        );
        continue;
      }
      if (trimmed === '---' || trimmed === '***') {
        out.push(<hr key={`hr-${key++}`} className="spacer-log-md__hr" />);
        continue;
      }

      out.push(
        <p key={`p-${key++}`} className="spacer-log-md__p">
          {inlineFormat(trimmed)}
        </p>
      );
    }

    flushList();
    flushCode();
    return out;
  }, [content]);

  return <div className="spacer-log-md ask-evans-prose">{nodes}</div>;
};

export default SpacerLogMarkdown;
